import { Api, UserMax, Mana } from "../handlers/types";
import { StorageService } from "./LocalStorageService";
import { EnergyService } from "./EnergyService";
import posthog from "posthog-js";

export class UserService {
  private api: Api<unknown>;
  private isFirstBalanceAccess: boolean = true;

  constructor(api: Api<unknown>) {
    this.api = api;
  }

  /**
   * Static factory method for creating and initializing a UserService
   */
  static async initialize(api: Api<unknown>): Promise<UserService> {
    const service = new UserService(api);
    await service.setup();
    return service;
  }

  /**
   * Set up the user service by loading user data
   */
  private async setup(): Promise<void> {
    try {
      await this.loadUser();
    } catch (error) {
      console.error("Failed to initialize UserService:", error);
    }
  }

  getUser(): UserMax {
    const user = StorageService.loadUser();
    if (!user) {
      throw new Error("User not found");
    }
    return user;
  }

  /**
   * Creates a new user via initCreate API
   */
  private async createUserViaInit(): Promise<UserMax> {
    try {
      const user = await this.api.api.webappInitCreate();
      if (user) {
        StorageService.saveUser(user);
        // Update energy data if available
        this.updateEnergyFromUser(user);

        // Identify user in PostHog
        posthog.identify(user.id.toString(), {
          firstName: user.firstName,
          secondName: user.secondName,
          username: user.username,
          lastLoginAt: user.lastLoginAt,
        });

        return user;
      }
      throw new Error("Failed to initialize user from webapp.initCreate");
    } catch (initError) {
      console.error("Failed to initialize user:", initError);
      throw initError;
    }
  }

  /**
   * Update energy data from a user object
   * Called whenever we get fresh user data from the backend
   */
  private updateEnergyFromUser(user: UserMax): void {
    // Check if the user object includes energy data
    if ("energy" in user) {
      EnergyService.updateFromUserModel(user);
    }
  }

  /**
   * Update mana data in the stored user object
   * Called when we receive updated mana data from the backend
   */
  updateManaData(manaData: Mana): void {
    try {
      const currentUser = StorageService.loadUser();
      if (currentUser) {
        const updatedUser: UserMax = {
          ...currentUser,
          mana: manaData,
        };
        StorageService.saveUser(updatedUser);
      }
    } catch (error) {
      console.error("Failed to update mana data:", error);
    }
  }

  async loadUser(): Promise<UserMax> {
    return this.createUserViaInit();
  }

  /**
   * Check if this is the first balance access and mark it as accessed
   * Returns true if should skip (first access), false if should proceed
   */
  shouldSkipFirstBalanceUpdate(): boolean {
    if (this.isFirstBalanceAccess) {
      this.isFirstBalanceAccess = false;
      return true; // Skip first access
    }
    return false; // Allow subsequent accesses
  }

  // Need to call webappinit each time
  // async loadUser(): Promise<User> {
  //   try {
  //     const response = await this.api.api.usersList()
  //     StorageService.saveUser(response)
  //     // Update energy data if available
  //     this.updateEnergyFromUser(response)
  //     return response
  //   } catch (error) {
  //     console.error('Failed to load user with userList:', error)
  //     // Fallback to initializing a fresh user via initCreate
  //     return this.createUserViaInit()
  //   }
  // }
}
